import { GridLinkOperator } from '@mui/x-data-grid-pro';

const sortValueMapper = new Map([
  ['1', 'asc'],
  ['-1', 'desc'],
]);

type Sort = 'asc' | 'desc';

interface ISortModel {
  field: string;
  sort: 'asc' | 'desc';
}

export const buildTableUrlParameters = (
  tableKey: string,
  queryString: string
): { [key: string]: string } => {
  if (queryString) {
    const queryParams = new URLSearchParams(queryString);
    return Array.from(queryParams.entries()).reduce(
      (hash: { [key: string]: string }, currentValue) => {
        const key = tableKey
          ? currentValue[0]
              .split('[')
              .map((w) => w.replace(']', ''))
              .map((w) => w.replace(/(.*)/, '[$1]'))
              .join('')
          : currentValue[0];
        hash[`${tableKey}${key}`] = currentValue[1];
        return hash;
      },
      {}
    );
  }
  return {};
};

export const buildSortModelFromUrlParameters = (
  tableKey: string,
  queryString: string
): ISortModel[] => {
  if (queryString) {
    const queryParams = new URLSearchParams(queryString);
    const keyStartsWith = tableKey ? `${tableKey}[sort]` : 'sort';
    const params = Array.from(queryParams.entries()).filter((p) =>
      p[0].startsWith(keyStartsWith)
    );
    return params
      .map((p) => {
        const field = p[0].replace(`${keyStartsWith}[`, '').replace(']', '');
        return {
          field,
          sort: sortValueMapper.get(p[1]) as Sort,
        };
      })
      .filter((p) => p.sort);
  }
  return [];
};

export const buildPaginationModelFromUrlParameters = (
  tableKey: string,
  queryString: string,
  defaultLimit = 100
) => {
  if (queryString) {
    const queryParams = new URLSearchParams(queryString);
    const keyStartsWith = tableKey ? `${tableKey}[pagination]` : 'pagination';
    const params = Array.from(queryParams.entries()).filter((p) =>
      p[0].startsWith(keyStartsWith)
    );
    const [, limit = defaultLimit] =
      params.find((p) => p[0].startsWith(`${keyStartsWith}[limit]`)) || [];
    const [, skip = 0] =
      params.find((p) => p[0].startsWith(`${keyStartsWith}[skip]`)) || [];
    return {
      pageSize: Number(limit),
      page: Number(skip) ? Number(skip) / Number(limit) : Number(skip),
    };
  }
  return {
    pageSize: defaultLimit,
    page: 0,
  };
};

export const buildFilterModelFromUrlParameters = (
  tableKey: string,
  queryString: string
) => {
  if (queryString) {
    const queryParams = new URLSearchParams(queryString);
    const keyStartsWith = tableKey ? `${tableKey}[filter]` : 'filter';
    const params = Array.from(queryParams.entries()).filter((p) =>
      p[0].startsWith(keyStartsWith)
    );
    if (params?.length) {
      const linkOperator = params[0][0].startsWith(`${keyStartsWith}[and]`)
        ? GridLinkOperator.And
        : GridLinkOperator.Or;
      const items = params.map((p, index) => {
        const [columnField, operatorValue] = p[0]
          .replace(`${keyStartsWith}[${linkOperator}][`, '')
          .replaceAll(']', '')
          .split('[');
        return {
          columnField,
          id: Number(`${Date.now()}${index}`),
          operatorValue,
          value: p[1],
        };
      });
      return {
        items: items,
        linkOperator: linkOperator,
        quickFilterValues: [],
        quickFilterLogicOperator: linkOperator,
      };
    }
  }
  return undefined;
};

export const buildFilterParamsFromUrlParameters = (
  tableKey: string,
  queryString: string
) => {
  const filterParams = new Map();
  if (queryString) {
    const queryParams = new URLSearchParams(queryString);
    const keyStartsWith = tableKey ? `${tableKey}[filter]` : 'filter';
    const params = Array.from(queryParams.entries()).filter((p) =>
      p[0].startsWith(keyStartsWith)
    );
    if (params?.length) {
      const linkOperator = params[0][0].startsWith(`${keyStartsWith}[and]`)
        ? GridLinkOperator.And
        : GridLinkOperator.Or;
      params.forEach((p) => {
        const [columnField] = p[0]
          .replace(`${keyStartsWith}[${linkOperator}][`, '')
          .replaceAll(']', '')
          .split('[');
        filterParams.set(columnField, p[1]);
      });
    }
  }
  return filterParams;
};
